/**
 * Admin Payments Page
 * List pending payments and confirm them
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CreditCard,
  Building2,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  ArrowLeft,
  Banknote,
  Receipt
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (cents) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(cents / 100);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PaymentCard = ({ payment, onConfirm, isConfirming }) => {
  const providerIcon = payment.provider === 'BANK' ? Banknote : CreditCard;
  const ProviderIcon = providerIcon;
  
  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: Payment Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <ProviderIcon className="h-5 w-5 text-slate-500" />
              <span className="font-semibold text-lg">{payment.order_id}</span>
              <Badge variant={payment.provider === 'BANK' ? 'outline' : 'secondary'}>
                {payment.provider === 'BANK' ? 'Bonifico' : 'Carta'}
              </Badge>
            </div>
            
            <div className="text-sm text-slate-600 space-y-1">
              {payment.workspace && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>{payment.workspace.name}</span>
                </div>
              )}
              {payment.plan && (
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  <span>{payment.plan.name} ({payment.plan.code})</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Creato: {formatDate(payment.created_at)}</span>
              </div>
            </div>
            
            {payment.description && (
              <p className="text-sm text-slate-500 mt-2 italic">{payment.description}</p>
            )}
          </div>
          
          {/* Right: Amount & Action */}
          <div className="flex flex-col items-end gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-slate-900">
                {formatPrice(payment.amount_cents)}
              </div>
              <div className="text-xs text-slate-500">IVA inclusa</div>
            </div>
            
            <Button
              onClick={() => onConfirm(payment.order_id)}
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isConfirming ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Conferma...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Conferma Pagamento
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminPayments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchPendingPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subscriptions/admin/payments/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Accesso non autorizzato. Solo gli amministratori possono accedere.');
        }
        throw new Error('Errore nel caricamento dei pagamenti');
      }
      
      const data = await response.json();
      setPayments(data.payments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPayments();
  }, [fetchPendingPayments]);

  const handleConfirmPayment = async (orderId) => {
    setConfirmingId(orderId);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subscriptions/admin/payments/${orderId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore nella conferma del pagamento');
      }
      
      const result = await response.json();
      
      if (result.already_paid) {
        setSuccessMessage(`Pagamento ${orderId} era già confermato.`);
      } else {
        setSuccessMessage(`Pagamento ${orderId} confermato con successo! Abbonamento attivato.`);
      }
      
      // Refresh list
      await fetchPendingPayments();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <PageHeader
            title="Pagamenti Piattaforma"
            subtitle="Pagamenti degli abbonamenti"
            actions={
              <>
                <Button
                  variant="outline"
                  className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                  size="sm"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Indietro
                </Button>
                <Button
                  variant="outline"
                  className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                  onClick={fetchPendingPayments}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Aggiorna
                </Button>
              </>
            }
          />
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-green-800">{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600">Caricamento pagamenti...</p>
            </CardContent>
          </Card>
        ) : payments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Nessun pagamento in attesa
              </h3>
              <p className="text-slate-600">
                Tutti i pagamenti sono stati processati.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  Pagamenti in Attesa
                </CardTitle>
                <CardDescription>
                  {payments.length} pagament{payments.length === 1 ? 'o' : 'i'} da confermare
                </CardDescription>
              </CardHeader>
            </Card>
            
            {payments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                onConfirm={handleConfirmPayment}
                isConfirming={confirmingId === payment.order_id}
              />
            ))}
          </>
        )}

        {/* Help Section */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Come funziona?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• I pagamenti con <strong>Bonifico</strong> devono essere confermati manualmente dopo aver verificato l'accredito.</li>
              <li>• Clicca su <strong>"Conferma Pagamento"</strong> per attivare l'abbonamento e generare la fattura.</li>
              <li>• L'operazione è idempotente: confermare un pagamento già confermato non causa duplicazioni.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
